// 일정 상세 화면의 "경기" 섹션 — 라운드별/코트별 토글 뷰.
//
// 빈 상태(아직 대진 없음) → 자동 생성 CTA, 기존 대진 → 재생성 + 라운드/코트
// 토글 + 매치 추가 CTA 흐름을 한 번에 그린다. matchCard 자체는 legacy/
// preact MatchCard에 그대로 두고 콜백으로 주입받아 closure 의존을 끊는다.

import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { isAdmin, isMember } from "../auth";
import { matchPlayerName } from "../matches";
import { DomNode, mountPreact } from "../ui/preact-mount";
import { formatTimeShort, surfaceLabel } from "../ui/format";

interface EventCourt {
  event_court_id: string;
  court_label?: string | null;
  surface_type?: string | null;
}

interface RoundInfo {
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  round_label?: string | null;
}

interface MatchLike {
  match_id?: string;
  round_number?: number | null;
  status?: string;
  result_status?: string;
  player_ids?: string[];
  event_court_id?: string | null;
  court_label?: string | null;
}

interface ParticipantLike {
  event_participant_id: string;
}

export interface MatchesSectionParams {
  ev: any;
  matches: MatchLike[];
  participants: ParticipantLike[];
  courts: EventCourt[];
  roundMap: Record<number, RoundInfo>;
  surfaceMap: Map<string, string>;
  canManage: boolean;
  matchCard: (m: MatchLike) => HTMLElement;
  openGenerate: (regenerate: boolean) => void;
  openAddMatch: (
    existingBonusRounds: number[],
    opts?: { initialCourtId?: string | null; initialCourtLabel?: string | null },
  ) => void;
}

function RoundView({
  byRound,
  roundMap,
  participants,
  allPids,
  matchCard,
}: {
  byRound: Map<number, MatchLike[]>;
  roundMap: Record<number, RoundInfo>;
  participants: ParticipantLike[];
  allPids: Set<string>;
  matchCard: (m: MatchLike) => HTMLElement;
}) {
  const entries = [...byRound.entries()];
  return (
    <Fragment>
      {entries.map(([rn, roundMatches]) => {
        const rd = roundMap[rn];
        const timeStr = rd
          ? `${formatTimeShort(rd.scheduled_start_at)} ~ ${formatTimeShort(rd.scheduled_end_at)}`
          : "";
        const playingIds = new Set<string>();
        for (const m of roundMatches) (m.player_ids || []).forEach((id) => playingIds.add(id));
        const resting = participants.filter((p) =>
          allPids.has(p.event_participant_id) && !playingIds.has(p.event_participant_id),
        );
        return (
          <Fragment key={rn}>
            <div class="round-label">
              <span>라운드 {rn}</span>
              {timeStr && <span class="round-label__time">{timeStr}</span>}
            </div>
            {roundMatches.map((m) => (
              <DomNode key={m.match_id} node={matchCard(m)} />
            ))}
            {resting.length > 0 && (
              <div class="round-rest">
                🪑 휴식: {resting.map((p) => matchPlayerName(p)).join(", ")}
              </div>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

function CourtView({
  matches,
  courts,
  participants,
  allPids,
  matchCard,
  onAddMatchToCourt,
}: {
  matches: MatchLike[];
  courts: EventCourt[];
  participants: ParticipantLike[];
  allPids: Set<string>;
  matchCard: (m: MatchLike) => HTMLElement;
  onAddMatchToCourt: ((courtId: string | null, courtLabel: string | null) => void) | null;
}) {
  const playingNow = new Set<string>();
  for (const m of matches) {
    if (m.status === "in_progress") (m.player_ids || []).forEach((id) => playingNow.add(id));
  }
  const restingNow = playingNow.size > 0
    ? participants.filter((p) =>
        allPids.has(p.event_participant_id) && !playingNow.has(p.event_participant_id),
      )
    : [];

  const byCourt = new Map<string, MatchLike[]>();
  const unassigned: MatchLike[] = [];
  for (const m of matches) {
    const cid = m.event_court_id;
    if (!cid) { unassigned.push(m); continue; }
    if (!byCourt.has(cid)) byCourt.set(cid, []);
    byCourt.get(cid)!.push(m);
  }
  const orderedCourts: EventCourt[] = courts.filter((c) => byCourt.has(c.event_court_id));
  for (const cid of byCourt.keys()) {
    if (!orderedCourts.find((oc) => oc.event_court_id === cid)) {
      orderedCourts.push({ event_court_id: cid, court_label: null });
    }
  }

  return (
    <Fragment>
      {restingNow.length > 0 && (
        <div class="court-rest-banner">
          🪑 지금 쉬는 중: {restingNow.map((p) => matchPlayerName(p)).join(", ")}
        </div>
      )}
      {orderedCourts.map((c) => {
        const list = (byCourt.get(c.event_court_id) || []).slice().sort(
          (a, b) => (a.round_number || 0) - (b.round_number || 0),
        );
        const label = c.court_label || (list[0] && list[0].court_label) || "코트";
        const surface = c.surface_type ? surfaceLabel(c.surface_type) : null;
        const inProgress = list.find((m) => m.status === "in_progress");
        const nextUp = list.find((m) =>
          m.status === "scheduled" && (m.result_status || "empty") === "empty",
        );
        const doneCount = list.filter((m) =>
          m.status === "completed" || (m.result_status && m.result_status !== "empty"),
        ).length;
        const remaining = list.length - doneCount - (inProgress ? 1 : 0);
        const rest = list.filter((m) => m !== inProgress && m !== nextUp);
        return (
          <Fragment key={c.event_court_id}>
            <div class="court-header">
              <span class="court-header__label">코트 {label}</span>
              {surface && <span class="court-header__surface">{surface}</span>}
              <span class="court-header__meta">
                {list.length}경기 · 완료 {doneCount}{remaining > 0 ? ` · 대기 ${remaining}` : ""}
              </span>
            </div>
            {inProgress && (
              <Fragment>
                <div class="court-section-label court-section-label--live">
                  <span class="court-dot court-dot--live" />
                  진행 중
                </div>
                <DomNode node={matchCard(inProgress)} />
              </Fragment>
            )}
            {nextUp && nextUp !== inProgress && (
              <Fragment>
                <div class="court-section-label court-section-label--next">지금</div>
                <DomNode node={matchCard(nextUp)} />
              </Fragment>
            )}
            {rest.length > 0 && (
              <Fragment>
                <div class="court-section-label">다음</div>
                {rest.map((m) => (
                  <DomNode key={m.match_id} node={matchCard(m)} />
                ))}
              </Fragment>
            )}
            {onAddMatchToCourt && (
              <button
                class="btn btn--ghost btn--block"
                type="button"
                style="margin-top:8px;margin-bottom:12px"
                onClick={() => onAddMatchToCourt(c.event_court_id, label)}
              >
                + 코트 {label} 매치 추가
              </button>
            )}
          </Fragment>
        );
      })}
      {unassigned.length > 0 && (
        <Fragment>
          <div class="court-header">
            <span class="court-header__label">코트 미배정</span>
            <span class="court-header__meta">{unassigned.length}경기</span>
          </div>
          {unassigned.map((m) => (
            <DomNode key={m.match_id} node={matchCard(m)} />
          ))}
        </Fragment>
      )}
    </Fragment>
  );
}

function ViewToggle({
  mode,
  onChange,
}: {
  mode: "court" | "round";
  onChange: (next: "court" | "round") => void;
}) {
  return (
    <div class="view-toggle">
      <button
        type="button"
        class={"view-toggle__btn" + (mode === "court" ? " view-toggle__btn--active" : "")}
        onClick={() => onChange("court")}
      >
        코트별
      </button>
      <button
        type="button"
        class={"view-toggle__btn" + (mode === "round" ? " view-toggle__btn--active" : "")}
        onClick={() => onChange("round")}
      >
        라운드별
      </button>
    </div>
  );
}

function MatchesSection(params: MatchesSectionParams) {
  const { ev, matches, participants, courts, roundMap,
    canManage, matchCard, openGenerate, openAddMatch } = params;

  const beforeStart = ev.status === "draft" || ev.status === "ready";
  const isEmpty = matches.length === 0;
  const isRegular = ev.event_type === "regular";
  const storageKey = `schedule-view-mode:${ev.event_id}`;

  // 정기모임 디폴트는 코트별. 비정기는 라운드만 의미 있음.
  // 사용자가 명시적으로 라운드별로 바꿨으면 localStorage 값을 존중.
  const [viewMode, setViewMode] = useState<"court" | "round">(() => {
    if (!isRegular) return "round";
    const stored = localStorage.getItem(storageKey);
    return (stored === "court" || stored === "round") ? stored : "court";
  });
  function changeMode(next: "court" | "round") {
    setViewMode(next);
    localStorage.setItem(storageKey, next);
  }

  const byRound = new Map<number, MatchLike[]>();
  if (!isEmpty) {
    for (const m of matches) {
      const rn = m.round_number || 0;
      if (!byRound.has(rn)) byRound.set(rn, []);
      byRound.get(rn)!.push(m);
    }
  }
  const existingBonusRounds = [...byRound.keys()].filter((rn) => {
    const rd = roundMap[rn];
    return !!rd && (rd.round_label || "").startsWith("보너스");
  });

  const allPids = new Set(participants.map((p) => p.event_participant_id));
  // 정기모임은 인원 적을 때 호스트가 코트에 있어도 운영이 안 멈추도록 활성
  // 클럽원 누구나 매치 추가 가능 (자유도 정책). 특별전은 staff/creator만.
  const canAddMatchAsMember = isRegular && (isAdmin() || isMember());
  const canAddMatch = (canManage || canAddMatchAsMember) && ev.status === "in_progress";
  const onAddMatchToCourt = canAddMatch
    ? (cid: string | null, lbl: string | null) =>
        openAddMatch(existingBonusRounds, { initialCourtId: cid, initialCourtLabel: lbl })
    : null;

  return (
    <div class="section">
      <div class="section__title">
        <h3>경기</h3>
        <span class="count">{matches.length}개</span>
      </div>

      {isEmpty ? (
        <Fragment>
          <div class="card empty">아직 대진이 없어요</div>
          {canManage && beforeStart && participants.length > 0 && (
            <button
              class="btn btn--ghost btn--block"
              type="button"
              onClick={() => openGenerate(false)}
            >
              스케줄 자동 생성
            </button>
          )}
        </Fragment>
      ) : (
        <Fragment>
          {canManage && beforeStart && (
            <Fragment>
              {ev.schedule_stale && (
                <div class="stale-notice">
                  참가자가 변경되었어요. 스케줄을 재생성해주세요.
                </div>
              )}
              <button
                class={ev.schedule_stale ? "btn btn--accent btn--block" : "btn btn--ghost btn--block"}
                type="button"
                style="margin-bottom:12px"
                onClick={() => openGenerate(true)}
              >
                스케줄 재생성
              </button>
            </Fragment>
          )}
          {isRegular && <ViewToggle mode={viewMode} onChange={changeMode} />}
          {viewMode === "court" ? (
            <CourtView
              matches={matches}
              courts={courts}
              participants={participants}
              allPids={allPids}
              matchCard={matchCard}
              onAddMatchToCourt={onAddMatchToCourt}
            />
          ) : (
            <RoundView
              byRound={byRound}
              roundMap={roundMap}
              participants={participants}
              allPids={allPids}
              matchCard={matchCard}
            />
          )}
        </Fragment>
      )}

      {canAddMatch && viewMode === "round" && (
        <button
          class="btn btn--ghost btn--block"
          type="button"
          style="margin-top:8px"
          onClick={() => openAddMatch(existingBonusRounds)}
        >
          + 매치 추가
        </button>
      )}
    </div>
  );
}

export function buildMatchesSection(params: MatchesSectionParams): HTMLElement {
  // surfaceMap is reserved for future per-match surface badges.
  void params.surfaceMap;
  return mountPreact(<MatchesSection {...params} />);
}
